import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from 'contexts/AuthContext';
import '../AuthModal/AuthModal.scss';

const UserIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
  </svg>
);
const MailIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
  </svg>
);
const LockIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
  </svg>
);
const EyeOnIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);
const EyeOffIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/>
    <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);

const extractError = (err) => {
  if (!err) return 'Неизвестная ошибка';
  if (typeof err === 'string') return err;
  if (err.detail) return String(err.detail);
  if (err.message) return String(err.message);
  if (err.username) return 'Username: ' + (Array.isArray(err.username) ? err.username[0] : err.username);
  if (err.email) return 'Email: ' + (Array.isArray(err.email) ? err.email[0] : err.email);
  if (err.password) return 'Пароль: ' + (Array.isArray(err.password) ? err.password[0] : err.password);
  const flat = Object.values(err).flat();
  return flat.length ? String(flat[0]) : JSON.stringify(err);
};

export default function RegisterForm({ onSuccess, onSwitchToLogin }) {
  const { register, verifyRegistration, resendVerificationCode } = useAuth();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showVerification, setShowVerification] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [verError, setVerError] = useState('');
  const [expiresAt, setExpiresAt] = useState(null);
  const [timer, setTimer] = useState('');
  const [usernameChecking, setUsernameChecking] = useState(false);
  const [usernameError, setUsernameError] = useState('');
  const [usernameAvailable, setUsernameAvailable] = useState(null);

  const checkUsername = async (usernameValue) => {
    if (!usernameValue || usernameValue.length < 3) {
      setUsernameError('');
      setUsernameAvailable(null);
      return;
    }
    
    setUsernameChecking(true);
    setUsernameError('');
    
    try {
      const response = await fetch('/api/account/check-username/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: usernameValue })
      });
      const data = await response.json();
      
      setUsernameAvailable(data.available);
      if (!data.available) {
        setUsernameError(data.message || 'Username уже занят');
      }
    } catch (err) {
      console.error('Username check error:', err);
    } finally {
      setUsernameChecking(false);
    }
  };

  React.useEffect(() => {
    if (!expiresAt) return;
    const update = () => {
      const diff = new Date(expiresAt) - new Date();
      if (diff <= 0) { setTimer('Код истёк'); return; }
      const m = Math.floor(diff / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setTimer(m + ':' + s.toString().padStart(2, '0'));
    };
    update();
    const iv = setInterval(update, 1000);
    return () => clearInterval(iv);
  }, [expiresAt]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!termsAccepted) { setError('Необходимо принять пользовательское соглашение для регистрации'); return; }
    if (password !== passwordConfirm) { setError('Пароли не совпадают'); return; }
    setLoading(true);
    const fd = new FormData();
    fd.append('username', username);
    fd.append('email', email);
    fd.append('password', password);
    fd.append('password_confirm', passwordConfirm);
    fd.append('first_name', '');
    fd.append('last_name', '');
    const res = await register(fd);
    setLoading(false);
    if (res.ok && res.data) {
      setRegisteredEmail(res.data.email || email);
      setExpiresAt(res.data.expires_at);
      setShowVerification(true);
    } else {
      setError(extractError(res.error));
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setVerError(''); setVerifying(true);
    const res = await verifyRegistration(registeredEmail, verificationCode);
    setVerifying(false);
    if (res.ok) {
      if (res.data && res.data.access) localStorage.setItem('token', res.data.access);
      setTimeout(() => window.location.reload(), 300);
    } else {
      setVerError(extractError(res.error));
    }
  };

  const handleResend = async () => {
    setVerError(''); setResending(true);
    const res = await resendVerificationCode(registeredEmail);
    setResending(false);
    if (res.ok) setExpiresAt(res.data && res.data.expires_at);
    else setVerError(extractError(res.error));
  };

  const verModal = showVerification && ReactDOM.createPortal(
    React.createElement('div', { className: 'am-overlay', onClick: (e) => e.target === e.currentTarget && setShowVerification(false) },
      React.createElement('div', { className: 'am-modal', onClick: (e) => e.stopPropagation() },
        React.createElement('div', { className: 'am-header' },
          React.createElement('h2', { className: 'am-title' }, 'Подтверждение'),
          React.createElement('button', { className: 'am-close', onClick: () => setShowVerification(false) },
            React.createElement('svg', { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: '2.5' },
              React.createElement('line', { x1: 18, y1: 6, x2: 6, y2: 18 }),
              React.createElement('line', { x1: 6, y1: 6, x2: 18, y2: 18 })
            )
          )
        ),
        React.createElement('p', { className: 'af-step-desc' }, 'Мы отправили 6-значный код на ', React.createElement('strong', null, registeredEmail)),
        verError && React.createElement('div', { className: 'af-error' }, verError),
        React.createElement('form', { onSubmit: handleVerify },
          React.createElement('div', { className: 'af-group' },
            React.createElement('label', { className: 'af-label' }, 'Код из письма'),
            React.createElement('div', { className: 'af-field' },
              React.createElement('span', { className: 'af-icon' }, React.createElement(LockIcon)),
              React.createElement('span', { className: 'af-sep' }),
              React.createElement('input', { className: 'af-input', value: verificationCode, onChange: (e) => setVerificationCode(e.target.value), required: true, placeholder: '     ', maxLength: 6, style: { letterSpacing: '4px', textAlign: 'center' }, autoFocus: true })
            ),
            timer && React.createElement('div', { style: { marginTop: 8, fontSize: 12, textAlign: 'center', color: timer === 'Код истёк' ? '#ff8585' : 'rgba(255,255,255,0.4)' } }, timer === 'Код истёк' ? 'Код истёк  запросите новый' : 'Действителен: ' + timer)
          ),
          React.createElement('button', { type: 'submit', className: 'af-submit-btn', disabled: verifying, style: { marginBottom: 10 } }, verifying ? 'Проверяем...' : 'Подтвердить')
        ),
        React.createElement('div', { style: { textAlign: 'center' } },
          React.createElement('button', { className: 'af-link-btn', onClick: handleResend, disabled: resending, type: 'button' }, resending ? 'Отправляем...' : 'Отправить код повторно')
        )
      )
    ),
    document.body
  );

  return React.createElement(React.Fragment, null,
    verModal,
    React.createElement('form', { onSubmit: handleSubmit },
      error && React.createElement('div', { className: 'af-error' }, error),
      React.createElement('div', { className: 'af-group' },
        React.createElement('label', { className: 'af-label' }, 'Username'),
        React.createElement('div', { className: 'af-field', style: usernameError ? { borderColor: '#ff8585' } : usernameAvailable === true ? { borderColor: '#4ade80' } : {} },
          React.createElement('span', { className: 'af-icon' }, React.createElement(UserIcon)),
          React.createElement('span', { className: 'af-sep' }),
          React.createElement('input', { 
            className: 'af-input', 
            value: username, 
            onChange: (e) => { setUsername(e.target.value); setUsernameError(''); setUsernameAvailable(null); }, 
            onBlur: (e) => checkUsername(e.target.value.trim()),
            required: true, 
            placeholder: 'Придумайте username', 
            autoComplete: 'username', 
            minLength: 3 
          }),
          usernameChecking && React.createElement('span', { style: { marginLeft: 8, fontSize: 12, color: 'rgba(255,255,255,0.5)' } }, '...'),
          usernameAvailable === true && React.createElement('span', { style: { marginLeft: 8, fontSize: 16 } }, '✓'),
          usernameAvailable === false && React.createElement('span', { style: { marginLeft: 8, fontSize: 16 } }, '✗')
        ),
        usernameError && React.createElement('div', { style: { marginTop: 4, fontSize: 12, color: '#ff8585' } }, usernameError)
      ),
      React.createElement('div', { className: 'af-group' },
        React.createElement('label', { className: 'af-label' }, 'Email'),
        React.createElement('div', { className: 'af-field' },
          React.createElement('span', { className: 'af-icon' }, React.createElement(MailIcon)),
          React.createElement('span', { className: 'af-sep' }),
          React.createElement('input', { className: 'af-input', type: 'email', value: email, onChange: (e) => setEmail(e.target.value), required: true, placeholder: 'Введите email', autoComplete: 'email' })
        )
      ),
      React.createElement('div', { className: 'af-group' },
        React.createElement('label', { className: 'af-label' }, 'Пароль'),
        React.createElement('div', { className: 'af-field' },
          React.createElement('span', { className: 'af-icon' }, React.createElement(LockIcon)),
          React.createElement('span', { className: 'af-sep' }),
          React.createElement('input', { className: 'af-input', type: showPassword ? 'text' : 'password', value: password, onChange: (e) => setPassword(e.target.value), required: true, placeholder: 'Минимум 8 символов', autoComplete: 'new-password' }),
          React.createElement('button', { type: 'button', className: 'af-eye', onClick: () => setShowPassword((v) => !v) }, showPassword ? React.createElement(EyeOffIcon) : React.createElement(EyeOnIcon))
        )
      ),
      React.createElement('div', { className: 'af-group' },
        React.createElement('label', { className: 'af-label' }, 'Повторите пароль'),
        React.createElement('div', { className: 'af-field' },
          React.createElement('span', { className: 'af-icon' }, React.createElement(LockIcon)),
          React.createElement('span', { className: 'af-sep' }),
          React.createElement('input', { className: 'af-input', type: showPassword ? 'text' : 'password', value: passwordConfirm, onChange: (e) => setPasswordConfirm(e.target.value), required: true, placeholder: 'Повторите пароль', autoComplete: 'new-password' })
        )
      ),
      React.createElement('div', { className: 'af-terms' },
        React.createElement('input', { id: 'terms-cb', type: 'checkbox', className: 'af-checkbox', checked: termsAccepted, onChange: (e) => setTermsAccepted(e.target.checked) }),
        React.createElement('label', { htmlFor: 'terms-cb' },
          'Нажимая Зарегистрироваться я принимаю ',
          React.createElement('a', { href: '/terms-and-conditions', target: '_blank', rel: 'noopener noreferrer', className: 'af-terms-link' }, 'пользовательское соглашение')
        )
      ),
      React.createElement('button', { type: 'submit', className: 'af-submit-btn', disabled: loading }, loading ? 'Регистрируем...' : 'Зарегистрироваться'),
      React.createElement('div', { className: 'af-footer' },
        React.createElement('span', null, 'Уже есть аккаунт?'),
        React.createElement('button', { type: 'button', className: 'af-footer-link', onClick: onSwitchToLogin }, 'Войти')
      )
    )
  );
}
